<template>
  <div class="register-page">
    <div class="register-container">
      <!-- Logo Section -->
      <div class="logo-section">
        <div class="logo">
          <span class="logo-icon">🏨</span>
        </div>
        <h1 class="app-title">Property Management</h1>
        <p class="app-subtitle">Professional Property Management System</p>
      </div>

      <!-- Success Message -->
      <div v-if="registrationSuccess" class="success-container">
        <div class="success-icon">✓</div>
        <h2 class="success-title">Registration Successful!</h2>
        <p class="success-message">
          Your account has been created and is pending approval.
          You will be notified by email once your account is activated.
        </p>
        <router-link to="/login" class="back-to-login-btn">
          Back to Login
        </router-link>
      </div>

      <!-- Registration Form -->
      <div v-else class="register-form-container">
        <form @submit.prevent="handleRegister" class="register-form">
          <h2 class="form-title">Create Account</h2>
          <p class="form-subtitle">Fill in your details to register</p>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            <span class="error-icon">⚠️</span>
            {{ errorMessage }}
          </div>

          <!-- Row 1: Username and Email -->
          <div class="form-row">
            <div class="form-group">
              <label for="username" class="form-label">Username</label>
              <div class="input-container">
                <span class="input-icon">👤</span>
                <input
                  type="text"
                  v-model="username"
                  id="username"
                  class="form-input"
                  placeholder="Username"
                  required
                  :disabled="isLoading"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="email" class="form-label">Email</label>
              <div class="input-container">
                <span class="input-icon">📧</span>
                <input
                  type="email"
                  v-model="email"
                  id="email"
                  class="form-input"
                  placeholder="Email"
                  required
                  :disabled="isLoading"
                />
              </div>
            </div>
          </div>

          <!-- Row 2: Password and Confirm -->
          <div class="form-row">
            <div class="form-group">
              <label for="password" class="form-label">Password</label>
              <div class="input-container">
                <span class="input-icon">🔒</span>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  id="password"
                  class="form-input"
                  placeholder="Password"
                  required
                  :disabled="isLoading"
                  minlength="8"
                />
                <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                  <span v-if="showPassword">👁️</span>
                  <span v-else>👁️‍🗨️</span>
                </button>
              </div>
            </div>

            <div class="form-group">
              <label for="confirmPassword" class="form-label">Confirm</label>
              <div class="input-container">
                <span class="input-icon">🔒</span>
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  v-model="confirmPassword"
                  id="confirmPassword"
                  class="form-input"
                  placeholder="Confirm"
                  required
                  :disabled="isLoading"
                />
                <button type="button" class="toggle-password" @click="showConfirmPassword = !showConfirmPassword">
                  <span v-if="showConfirmPassword">👁️</span>
                  <span v-else>👁️‍🗨️</span>
                </button>
              </div>
            </div>
          </div>
          <p class="field-hint">Password must be at least 8 characters</p>

          <!-- Submit Button -->
          <button type="submit" class="submit-btn" :disabled="isLoading">
            <span v-if="isLoading" class="loading-spinner"></span>
            <span v-if="isLoading">Creating Account...</span>
            <span v-else>Create Account</span>
          </button>

          <!-- Login Link -->
          <div class="login-link">
            <span>Already have an account?</span>
            <router-link to="/login" class="link">Sign In</router-link>
          </div>
        </form>
      </div>
    </div>

    <!-- Background Elements -->
    <div class="background-elements">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>
  </div>
</template>

<script>
import SETTINGS from '@/config/settings.js';

export default {
  name: 'RegisterView',
  data() {
    return {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      errorMessage: '',
      isLoading: false,
      registrationSuccess: false,
      showPassword: false,
      showConfirmPassword: false
    };
  },

  methods: {
    async handleRegister() {
      this.errorMessage = '';

      // Validate passwords match
      if (this.password !== this.confirmPassword) {
        this.errorMessage = 'Passwords do not match.';
        return;
      }

      // Validate password length
      if (this.password.length < 8) {
        this.errorMessage = 'Password must be at least 8 characters long.';
        return;
      }

      this.isLoading = true;

      const registerData = {
        username: this.username,
        email: this.email,
        password: this.password,
        password_confirm: this.confirmPassword
      };

      try {
        const response = await fetch(`${SETTINGS.API.baseUrl}/api/users/register/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(registerData),
        });

        const data = await response.json();

        if (response.ok) {
          this.registrationSuccess = true;
        } else {
          // Handle validation errors
          if (data.email) {
            throw new Error(Array.isArray(data.email) ? data.email[0] : data.email);
          } else if (data.username) {
            throw new Error(Array.isArray(data.username) ? data.username[0] : data.username);
          } else if (data.password) {
            throw new Error(Array.isArray(data.password) ? data.password[0] : data.password);
          } else if (data.detail) {
            throw new Error(data.detail);
          } else if (data.message) {
            throw new Error(data.message);
          } else {
            throw new Error('Registration failed. Please try again.');
          }
        }
      } catch (error) {
        console.error('Registration error:', error);
        this.errorMessage = error.message;
      } finally {
        this.isLoading = false;
      }
    }
  },

  mounted() {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
.register-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.bg-circle-1 {
  width: 300px;
  height: 300px;
  top: -150px;
  left: -150px;
  animation-delay: 0s;
}

.bg-circle-2 {
  width: 200px;
  height: 200px;
  top: 50%;
  right: -100px;
  animation-delay: 2s;
}

.bg-circle-3 {
  width: 150px;
  height: 150px;
  bottom: -75px;
  left: 30%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.register-container {
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  width: 100%;
  max-width: 550px;
  position: relative;
  z-index: 2;
  animation: slideUp 0.8s ease-out;
}

@keyframes slideUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 30px;
  text-align: center;
  color: white;
}

.logo {
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.logo-icon {
  font-size: 28px;
}

.app-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
}

.app-subtitle {
  font-size: 13px;
  margin: 0;
  opacity: 0.9;
  font-weight: 400;
}

.register-form-container {
  padding: 30px;
}

.form-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 6px 0;
  text-align: center;
}

.form-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 25px 0;
  text-align: center;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.error-icon {
  font-size: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 18px;
}

.form-row .form-group {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 18px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.field-hint {
  font-size: 12px;
  color: #64748b;
  margin: 4px 0 0 0;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  z-index: 2;
  font-size: 14px;
  opacity: 0.7;
}

.form-input {
  width: 100%;
  padding: 14px 14px 14px 46px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-password {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.toggle-password:hover {
  opacity: 1;
}

.submit-btn {
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.login-link {
  text-align: center;
  font-size: 14px;
  color: #64748b;
}

.login-link .link {
  color: #667eea;
  font-weight: 600;
  text-decoration: none;
  margin-left: 6px;
  transition: color 0.3s ease;
}

.login-link .link:hover {
  color: #764ba2;
  text-decoration: underline;
}

/* Success State */
.success-container {
  padding: 40px 30px;
  text-align: center;
}

.success-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: white;
  font-weight: bold;
}

.success-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 12px 0;
}

.success-message {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 25px 0;
}

.back-to-login-btn {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  padding: 12px 30px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.back-to-login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Responsive Design */
@media (max-width: 480px) {
  .register-page {
    padding: 15px;
  }

  .register-container {
    max-width: 100%;
  }

  .logo-section {
    padding: 25px 20px;
  }

  .register-form-container {
    padding: 25px 20px;
  }

  .app-title {
    font-size: 20px;
  }

  .form-title {
    font-size: 18px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .form-row .form-group {
    margin-bottom: 18px;
  }
}
</style>
